import type { KeyboardEvent, MouseEvent } from 'react';
import { forwardRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import type {
  FontValue,
  TextStyleValue
} from '@elfsight/configurator-contracts';
import { copyTextToClipboard, getColorWithAlpha } from '@elfsight/lib-utils';
import { Popout } from '@elfsight/widget-modal';
import {
  effect,
  roundTo4,
  to4VTuple,
  transition
} from '@elfsight/widget-utils';
import useMergedRef from '@react-hook/merged-ref';
import { motion } from 'framer-motion';
import { ellipsisMixin } from '../mixins';

const RESET_TIMEOUT = 3000;

export interface CouponProps {
  children: string;
  color?: string;
  font?: TextStyleValue;
  fontFamily?: FontValue;
  fontColor?: string;
  borderRadius?: number;
  borderColor?: string;
  backgroundColor?: string;
  backgroundHoverColor?: string;
  width?: number | string;
  height?: number;
  messages?: Record<string, string>;
  onCopied?: (value: string) => void;
}

export const Coupon = forwardRef<HTMLDivElement, CouponProps>(function Coupon(
  {
    children,
    color = 'rgb(17, 17, 17)',
    font = {
      fontSize: 16,
      fontWeight: 'bold',
      fontStyle: 'normal'
    },
    fontFamily,
    fontColor,
    borderRadius = 2,
    borderColor: derivedBorderColor,
    backgroundColor: derivedBackgroundColor,
    backgroundHoverColor: derivedBackgroundHoverColor,
    height,
    width,
    messages = {
      copied: 'Copied!',
      clickToCopy: 'Click To Copy'
    },
    onCopied
  },
  forwardingRef
) {
  const [element, setElement] = useState<HTMLElement | null>(null);
  const mergedRef = useMergedRef(forwardingRef, setElement);

  const [isCopied, setCopied] = useState(false);

  const [hover, setHover] = useState(false);
  const [focus, setFocus] = useState(false);
  const isInteracting = hover || focus;

  const { fontWeight, fontStyle, fontSize } = font;

  const currentFontColor = fontColor || color;
  const currentBorderColor =
    derivedBorderColor || getColorWithAlpha(color, 0.5);
  const currentBackgroundColor =
    derivedBackgroundColor || getColorWithAlpha(color, 0.05);
  const currentBackgroundHoverColor =
    derivedBackgroundHoverColor || getColorWithAlpha(color, 0.1);

  const [paddingTop, paddingRight, paddingBottom, paddingLeft] = to4VTuple([
    roundTo4(fontSize / 1.5),
    roundTo4(fontSize / 2)
  ]);

  const copy = (text: string | number) => {
    const str = text.toString();
    copyTextToClipboard(str);
    setCopied(true);

    if (element) {
      element.blur();
    }

    onCopied?.(str);
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      copy(children);
    }
  };

  const handleClick = (e: MouseEvent) => {
    e.stopPropagation();
    copy(children);
  };

  useEffect(() => {
    let timeout: number;

    if (isCopied) {
      timeout = window.setTimeout(() => setCopied(false), RESET_TIMEOUT);
    }

    return () => clearTimeout(timeout);
  }, [isCopied]);

  return (
    <Container
      ref={mergedRef}
      tabIndex={0}
      onHoverStart={() => setHover(true)}
      onHoverEnd={() => setHover(false)}
      onFocus={() => setFocus(true)}
      onBlur={() => setFocus(false)}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      style={{
        paddingTop,
        paddingRight,
        paddingBottom,
        paddingLeft,
        borderColor: currentBorderColor,
        borderRadius,
        height,
        width,
        fontSize,
        color: currentFontColor
      }}
      transition={transition.default}
      animate={{
        backgroundColor: isInteracting
          ? currentBackgroundHoverColor
          : currentBackgroundColor
      }}
    >
      <Popout
        show={isCopied}
        anchorElement={element}
        direction="upward"
        alignment="center"
        offsetTop={-4}
      >
        <CopiedTextTooltip
          transition={transition.default}
          {...effect.fade}
          role="tooltip"
        >
          {messages.copied}
        </CopiedTextTooltip>
      </Popout>

      <Inner>
        <HiddenClickToCopyText _small={fontSize < 15}>
          {messages.clickToCopy}
        </HiddenClickToCopyText>
        <ClickToCopyText
          transition={transition.default}
          initial={{ opacity: 0 }}
          animate={{ opacity: isInteracting && !isCopied ? 1 : 0 }}
          _small={fontSize < 15}
        >
          {messages.clickToCopy}
        </ClickToCopyText>
        <Value
          transition={transition.default}
          style={{
            fontFamily: fontFamily?.value,
            fontWeight,
            fontStyle
          }}
          animate={{
            transform: `translateY(${isInteracting && !isCopied ? 2 : 0}px)`
          }}
        >
          {children}
        </Value>
      </Inner>
    </Container>
  );
});

export const Container = styled(motion.div)`
  cursor: pointer;
  position: relative;
  border-style: dashed;
  border-width: 1px;
  border-radius: 2px;
  overflow: hidden;
  outline: none;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ClickToCopyText = styled(motion.div)<{ _small: boolean }>`
  line-height: 1;
  font-size: ${({ _small }) => (_small ? 8 : 11)}px;
  text-transform: uppercase;
  position: absolute;
  left: 50%;
  bottom: 100%;
  transform: translate(-50%, 4px);
  ${ellipsisMixin};
`;

export const HiddenClickToCopyText = styled.div<{ _small: boolean }>`
  line-height: 1;
  font-size: ${({ _small }) => (_small ? 8 : 11)}px;
  text-transform: uppercase;
  height: 0;
  visibility: hidden;
  ${ellipsisMixin};
`;

export const CopiedTextTooltip = styled(motion.div)`
  font-size: 13px;
  padding: 6px 8px;
  border-radius: 4px;
  box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.3);
  background-color: rgb(17, 17, 17);
  color: rgb(255, 255, 255);
`;

export const Inner = styled.div`
  position: relative;
  text-align: center;
  flex: 1;
  min-width: 0;
`;

export const Value = styled(motion.div)`
  line-height: 1.32;
  ${ellipsisMixin};
`;
